/**
 * calculations.js
 * 
 * Cost calculation functions for defect forms.
 * Handles base cost, element affected %, extra costs, and multiple priority distribution.
 * 
 * Exports:
 * - calulateTotal: Calculate total cost with all factors
 * - convertToDecimal: Format total as decimal string
 * - calculateDistributedCosts: Distribute costs across short/medium/long priorities
 */

/**
 * Calculate total cost including all factors
 * 
 * @param {Object} values - Form values
 * @param {boolean} includeMultiple - Whether to include multiple priority distribution
 * @returns {number} - Total calculated cost
 */
export const calulateTotal = (values, includeMultiple) => {
  let total = 0;
  if (values.pricePerUnit > 0 && values.quantity > 0) {
    total = values.pricePerUnit * values.quantity;
    if (values.elementAffected > 0) {
      total = total * (values.elementAffected / 100);
    }
    if (values.extraCost > 0) {
      total = total + Number(values.extraCost);
    }
    if (values.isMultiple && includeMultiple) {
      total =
        Math.round((total * values.multipleCost?.short) / 1000) * 1000 +
        Math.round((total * values.multipleCost?.medium) / 1000) * 1000 +
        Math.round((total * values.multipleCost?.long) / 1000) * 1000;
    }
    return total;
  }
  return total;
};

/**
 * Convert calculated total to decimal string format
 * 
 * @param {Object} value - Form values
 * @param {boolean} isMultiple - Whether multiple priority is enabled
 * @returns {string} - Formatted decimal string (e.g., "1234.56")
 */
export const convertToDecimal = (value, isMultiple) => {
  return parseFloat(calulateTotal(value, isMultiple)).toFixed(2)
}

/**
 * Calculate distributed costs for Multiple Priority ensuring exact total match.
 * Rounds each portion independently, then adjusts the largest portion to absorb any rounding difference.
 * 
 * @param {number} total - The total cost to distribute (should already be rounded to nearest 1000)
 * @param {number} shortPct - Short term priority percentage (0-1, e.g., 0.25 for 25%)
 * @param {number} mediumPct - Medium term priority percentage (0-1, e.g., 0.60 for 60%)
 * @param {number} longPct - Long term priority percentage (0-1, e.g., 0.15 for 15%)
 * @returns {{short: number, medium: number, long: number}} - Distributed costs that sum exactly to total
 */
export const calculateDistributedCosts = (total, shortPct, mediumPct, longPct) => {
  // Round each portion independently (to nearest 1000)
  const shortCost = Math.round((total * shortPct) / 1000) * 1000;
  const mediumCost = Math.round((total * mediumPct) / 1000) * 1000;
  const longCost = Math.round((total * longPct) / 1000) * 1000;
  
  // Calculate the sum and difference from original total
  const sum = shortCost + mediumCost + longCost;
  const difference = total - sum;
  
  // If there's a difference, adjust the largest portion to maintain exact total
  if (difference !== 0) {
    // Find which portion is largest (in absolute value)
    const portions = [
      { name: 'short', value: shortCost },
      { name: 'medium', value: mediumCost },
      { name: 'long', value: longCost }
    ];
    
    const largest = portions.reduce((max, current) => 
      Math.abs(current.value) > Math.abs(max.value) ? current : max
    );
    
    // Adjust the largest portion
    if (largest.name === 'short') {
      return { short: shortCost + difference, medium: mediumCost, long: longCost };
    } else if (largest.name === 'medium') {
      return { short: shortCost, medium: mediumCost + difference, long: longCost };
    } else {
      return { short: shortCost, medium: mediumCost, long: longCost + difference };
    }
  }
  
  // No adjustment needed, return as is
  return { short: shortCost, medium: mediumCost, long: longCost };
};
